// src/services/storageProxy.js
// Proxy para Storage de Supabase - oculta URLs directas
import { supabase } from '../config/supabase';

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};

export const storageProxy = {
  /**
   * Lista archivos en un bucket
   * @param {string} bucket - Nombre del bucket (ej: 'gymnastics-images')
   * @param {string} path - Ruta dentro del bucket (ej: 'clubs', 'judges')
   * @param {number} limit - Límite de resultados (default: 100)
   */
  async list(bucket, path = '', limit = 100) {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      const encodedBucket = encodeURIComponent(bucket);
      const encodedPath = path ? path.split('/').map(encodeURIComponent).join('/') : '';
      const url = `/api/v1/storage/list/${encodedBucket}${encodedPath ? `/${encodedPath}` : ''}?limit=${limit}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const json = await res.json();
      if (!res.ok) {
        return { data: null, error: json.error || 'Error listando archivos' };
      }

      return { data: json.data, error: null };
    } catch (err) {
      console.error('[storageProxy] Error en list:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Obtiene URL pública de un archivo
   * @param {string} bucket - Nombre del bucket
   * @param {string} filePath - Ruta completa del archivo (ej: 'clubs/file.jpg')
   */
  async getPublicUrl(bucket, filePath) {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      const encodedBucket = encodeURIComponent(bucket);
      const encodedFilePath = filePath.split('/').map(encodeURIComponent).join('/');
      const url = `/api/v1/storage/public/${encodedBucket}/${encodedFilePath}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const json = await res.json();
      if (!res.ok) {
        return { data: null, error: json.error || 'Error obteniendo URL pública' };
      }

      return { data: json.data, error: null };
    } catch (err) {
      console.error('[storageProxy] Error en getPublicUrl:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Helper: Busca archivo por patrón en un path
   * @param {string} bucket - Nombre del bucket
   * @param {string} path - Ruta donde buscar
   * @param {string} pattern - Patrón a buscar en el nombre del archivo
   */
  async findFile(bucket, path, pattern) {
    try {
      const { data: files, error } = await this.list(bucket, path, 100);
      if (error) return { data: null, error };

      const match = (files || []).find(f => f.name.includes(pattern));
      if (!match) return { data: null, error: null };

      const fullPath = path ? `${path}/${match.name}` : match.name;
      const { data: urlData, error: urlError } = await this.getPublicUrl(bucket, fullPath);
      
      if (urlError) return { data: null, error: urlError };
      return { data: urlData?.publicUrl || null, error: null };
    } catch (err) {
      console.error('[storageProxy] Error en findFile:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Sube un archivo al storage
   * @param {string} bucket - Nombre del bucket (ej: 'gymnastics-images')
   * @param {string} filePath - Ruta completa donde guardar (ej: 'clubs/logo-123.jpg')
   * @param {File} file - Archivo a subir (objeto File del navegador)
   * @param {Object} options - Opciones adicionales (cacheControl, upsert)
   */
  async upload(bucket, filePath, file, options = {}) {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', file);

      const encodedBucket = encodeURIComponent(bucket);
      const encodedFilePath = filePath.split('/').map(encodeURIComponent).join('/');
      const url = `/api/v1/storage/upload/${encodedBucket}/${encodedFilePath}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();
      if (!res.ok) {
        return { success: false, data: null, error: json.error || 'Error subiendo archivo' };
      }

      return { success: true, data: json.data, error: null };
    } catch (err) {
      console.error('[storageProxy] Error en upload:', err.message);
      return { success: false, data: null, error: err.message };
    }
  },
};
